import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Bank,
  Plus,
  X,
  FloppyDisk,
  FilePdf,
  FileXls,
  Sparkle,
  Trash,
  WarningCircle,
  UploadSimple,
  Percent,
  TrendUp,
  Target,
  Calculator,
  Clock,
  CheckCircle,
  ArrowRight,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import BulkUploadDialog, { PortfolioSummary } from "../components/loans/BulkUploadDialog";
import BonusAdvisor from "../components/loans/BonusAdvisor";
import PrepaymentSimulator from "../components/loans/PrepaymentSimulator";
import RateChangeDialog from "../components/loans/RateChangeDialog";
import PortfolioRateChangeDialog from "../components/loans/PortfolioRateChangeDialog";
import ProgressReports from "../components/loans/ProgressReports";
import AiSuggestionsPanel from "../components/loans/ai/AiSuggestionsPanel";
import { formatINR } from "../lib/loanMath";

const STRATEGIES = [
  { id: "reduce_tenure", label: "Reduce tenure (max savings)" },
  { id: "reduce_emi", label: "Reduce EMI (more cashflow)" },
];

const KINDS = [
  { id: "loan", label: "Loan (fixed tenure)" },
  { id: "overdraft", label: "Overdraft (with dropline)" },
];

const BENCHMARK_TYPES = [
  "Repo-linked (EBLR)",
  "MCLR",
  "Base Rate/BPLR",
  "Fixed",
  "Other",
];

const blankLoan = (i = 1, kind = "loan") => ({
  _k: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
  name: kind === "overdraft" ? `Overdraft ${i}` : `Loan ${i}`,
  kind,
  principal: 5000000,
  interest_rate_pct: 8.5,
  tenure_years: 15,
  current_balance: 4500000,
  dropline_years: kind === "overdraft" ? 10 : null,
  part_payment_amount: 0,
  part_payment_strategy: "reduce_tenure",
  // Feature 4 additive fields — safe defaults, never block existing flows
  benchmark_type: "Other",
  next_reset_date: "",
});

/**
 * Loan Rebalancer — multi-loan + overdraft simulator.
 * Adds/removes loans dynamically, computes total EMI, projects part-payment
 * impact, exports to PDF/Excel, saves the plan, and offers Claude AI suggestion.
 */
export default function LoanRebalancer() {
  const [loans, setLoans] = useState([]);
  const [planName, setPlanName] = useState("My loan plan");
  const [savedPlans, setSavedPlans] = useState([]);
  const [result, setResult] = useState(null);
  const [calc, setCalc] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState("my-loans");
  const [tourStep, setTourStep] = useState(() => {
    try {
      return localStorage.getItem("loanRebalancer_tourDone") === "1" ? 0 : 1;
    } catch { return 0; }
  });

  // What-if overlay sliders (live, no resubmit). Same UX as Snowball v2:
  //  - pp_bonus           — extra one-time prepayment applied to EVERY loan
  //  - emi_bump_per_mo    — extra ₹ paid every month (modelled as 12×bump
  //                         added to annual prepayment, since the rebalance
  //                         endpoint already simulates prepay impact)
  //  - tenure_cut_pct     — % of remaining tenure trimmed off every loan
  const [whatIf, setWhatIf] = useState({ pp_bonus: 0, emi_bump_per_mo: 0, tenure_cut_pct: 0 });
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [whatIfBusy, setWhatIfBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [rateDialog, setRateDialog] = useState({ open: false, loanIdx: null });
  const [portfolioRateOpen, setPortfolioRateOpen] = useState(false);
  const [prepayPrefill, setPrepayPrefill] = useState(null);

  const onBulkImported = async (result) => {
    // Reload the imported plan into the UI
    await loadSaved();
    try {
      const { data } = await api.get("/loans/saved");
      const plan = (data || []).find((p) => p.name === result.plan_name);
      if (plan) {
        loadPlan(plan);
      }
    } catch (err) {
      // Reload failed after bulk import; previous (correct) results stay on screen.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("post-bulk reload failed:", err?.message);
      }
    }
    setBulkOpen(false);
  };

  const loadSaved = async () => {
    try {
      const { data } = await api.get("/loans/saved");
      setSavedPlans(data || []);
    } catch (err) {
      // 401 (not logged in) is expected for anonymous users; only log unexpected failures.
      if (err?.response?.status !== 401) {
        console.warn("[LoanRebalancer] loadSaved failed:", err?.response?.status || err?.message);
      }
    }
  };

  useEffect(() => {
    loadSaved();
  }, []);

  const payload = useMemo(
    () => ({
      name: planName,
      loans: loans.map((l) => ({
        id: l.id,
        name: l.name,
        kind: l.kind,
        principal: Number(l.principal) || 0,
        interest_rate_pct: Number(l.interest_rate_pct) || 0,
        tenure_years: Number(l.tenure_years) || 1,
        current_balance:
          l.current_balance === "" || l.current_balance === null
            ? null
            : Number(l.current_balance),
        dropline_years: l.kind === "overdraft" ? Number(l.dropline_years) || 10 : null,
        part_payment_amount: Number(l.part_payment_amount) || 0,
        part_payment_strategy: l.part_payment_strategy || "reduce_tenure",
        // additive Feature 4 fields — only passed when set, old code paths ignore them
        ...(l.benchmark_type ? { benchmark_type: l.benchmark_type } : {}),
        ...(l.next_reset_date ? { next_reset_date: l.next_reset_date } : {}),
        ...(l.current_emi ? { current_emi: Number(l.current_emi) } : {}),
        ...(l.remaining_tenure_months ? { remaining_tenure_months: Number(l.remaining_tenure_months) } : {}),
        ...(l.rate_type ? { rate_type: l.rate_type } : {}),
      })),
    }),
    [loans, planName],
  );

  const addLoan = (kind) =>
    setLoans((prev) => [...prev, blankLoan(prev.length + 1, kind)]);
  const removeLoan = (i) =>
    setLoans((prev) => prev.filter((_, j) => j !== i));
  const updLoan = (i, k, v) =>
    setLoans((prev) => prev.map((row, j) => (j === i ? { ...row, [k]: v } : row)));

  const runCalc = async () => {
    if (loans.length === 0) {
      toast.error("Add at least one loan");
      return;
    }
    setCalc(true);
    try {
      const { data } = await api.post("/loans/rebalance", payload);
      setResult(data);
      // Clear any pending what-if overlay so the baseline is fresh.
      setWhatIf({ pp_bonus: 0, emi_bump_per_mo: 0, tenure_cut_pct: 0 });
      setWhatIfResult(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Calc failed");
    } finally {
      setCalc(false);
    }
  };

  // Build a tweaked payload from `whatIf` overlay. Pure transformation — does
  // not depend on `result` so we can run it through the rebalance endpoint
  // without touching the backend.
  const overlayPayload = useCallback((overlay) => ({
    ...payload,
    loans: payload.loans.map((l) => {
      const extraAnnual = (overlay.emi_bump_per_mo || 0) * 12;
      const trim = Math.max(0, Math.min(0.9, (overlay.tenure_cut_pct || 0) / 100));
      return {
        ...l,
        part_payment_amount: (l.part_payment_amount || 0) + (overlay.pp_bonus || 0) + extraAnnual,
        tenure_years: Math.max(1, Math.round((l.tenure_years || 1) * (1 - trim))),
        dropline_years: l.kind === "overdraft" && l.dropline_years
          ? Math.max(1, Math.round(l.dropline_years * (1 - trim)))
          : l.dropline_years,
      };
    }),
  }), [payload]);

  // Debounced re-fetch whenever any what-if slider moves and we have a baseline.
  useEffect(() => {
    if (!result) return undefined;
    const idle = !whatIf.pp_bonus && !whatIf.emi_bump_per_mo && !whatIf.tenure_cut_pct;
    if (idle) { setWhatIfResult(null); return undefined; }
    setWhatIfBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/loans/rebalance", overlayPayload(whatIf));
        setWhatIfResult(data);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("loan what-if failed:", err?.response?.data?.detail || err.message);
        }
      } finally {
        setWhatIfBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [whatIf, result, overlayPayload]);

  const resetWhatIf = () => {
    setWhatIf({ pp_bonus: 0, emi_bump_per_mo: 0, tenure_cut_pct: 0 });
    setWhatIfResult(null);
  };
  const view = whatIfResult || result;

  const savePlan = async () => {
    setSaving(true);
    try {
      await api.post("/loans/save", payload);
      toast.success(`Plan "${planName}" saved`);
      loadSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Login required to save");
    } finally {
      setSaving(false);
    }
  };

  const loadPlan = (plan) => {
    setLoans(plan.loans.map((l, i) => ({ ...l, _k: `${Date.now()}-${i}` })));
    setPlanName(plan.name);
    setResult(null);
    toast.success(`Loaded "${plan.name}"`);
  };

  const deletePlan = async (name) => {
    if (!window.confirm(`Delete saved plan "${name}"?`)) return;
    try {
      await api.delete(`/loans/saved/${encodeURIComponent(name)}`);
      toast.success("Deleted");
      loadSaved();
    } catch {
      toast.error("Could not delete");
    }
  };

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      const r = await api.post(`/loans/export.${kind}`, payload, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `estima-loans.${kind}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting("");
    }
  };

  const askAi = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/loans/ai-suggest", payload);
      setAiResult(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "AI is busy. Try again in a moment.";
      setAiResult({ error: msg });
    } finally {
      setAiLoading(false);
    }
  };

  const downloadTemplate = useCallback(async () => {
    try {
      const r = await api.get("/loans/template.xlsx", { responseType: "blob" });
      const blob = new Blob([r.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Estima_Loan_Upload_Template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template.");
    }
  }, []);

  const dismissTour = useCallback(() => {
    try { localStorage.setItem("loanRebalancer_tourDone", "1"); } catch { /* ignore */ }
    setTourStep(0);
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-12" data-testid="loan-rebalancer-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Bank size={14} weight="duotone" /> Loan Rebalancer
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl">Stack every EMI in one view.</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Add every home loan, car loan, personal loan, and overdraft you carry. See total monthly outflow, simulate part-payments, and find the cheapest path to debt-free.
          </p>
        </div>
      </div>

      {savedPlans.length > 0 && (
        <div className="card-flat p-4 mb-6" data-testid="loan-saved-plans">
          <div className="eyebrow mb-2">Saved plans</div>
          <div className="flex gap-2 flex-wrap">
            {savedPlans.map((p) => (
              <div key={p.name} className="flex items-center gap-1 border hairline px-2 py-1 text-xs">
                <button
                  onClick={() => loadPlan(p)}
                  className="hover:text-[hsl(var(--secondary))]"
                  data-testid={`loan-saved-load-${p.name}`}
                >
                  {p.name}
                </button>
                <button
                  onClick={() => deletePlan(p.name)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={`Delete ${p.name}`}
                  data-testid={`loan-saved-del-${p.name}`}
                >
                  <Trash size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="loan-tabs">
        <TabsList className="h-auto flex-wrap justify-start p-1 w-full overflow-x-auto">
          <TabsTrigger value="my-loans" data-testid="tab-my-loans">My Loans</TabsTrigger>
          <TabsTrigger value="bonus-advisor" data-testid="tab-bonus-advisor">Bonus Advisor</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="tab-simulator">Prepayment Simulator</TabsTrigger>
          <TabsTrigger value="rate-changes" data-testid="tab-rate-changes">Rate Changes</TabsTrigger>
          <TabsTrigger value="scenarios" data-testid="tab-scenarios">Saved Scenarios</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress">Progress &amp; Reports</TabsTrigger>
          <TabsTrigger value="ai-suggestions" data-testid="tab-ai-suggestions">AI Suggestions</TabsTrigger>
        </TabsList>

        <TabsContent value="my-loans" className="mt-6">
          {loans.length === 0 ? (
            <EmptyLoansState
              onAdd={() => addLoan("loan")}
              onUpload={() => setBulkOpen(true)}
              onDownloadTemplate={downloadTemplate}
            />
          ) : (
            <div className="grid lg:grid-cols-[1fr_440px] gap-8">
              <div className="space-y-4">
                <div className="card-flat p-4 flex items-center gap-3 flex-wrap" data-tour="actions-bar">
                  <input
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    className="input-dark px-3 py-2 text-sm flex-1 min-w-[200px]"
                    placeholder="Plan name"
                    data-testid="loan-plan-name"
                  />
                  <button onClick={() => addLoan("loan")} className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid="loan-add-loan">
                    <Plus size={12} weight="bold" /> Add loan
                  </button>
                  <button onClick={() => addLoan("overdraft")} className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid="loan-add-od">
                    <Plus size={12} weight="bold" /> Add overdraft
                  </button>
                  <button
                    onClick={() => setBulkOpen(true)}
                    className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 border hairline rounded-md"
                    data-testid="loan-bulk-upload"
                  >
                    <UploadSimple size={12} weight="bold" /> Upload Excel
                  </button>
                  <button
                    onClick={downloadTemplate}
                    className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 border hairline rounded-md"
                    data-testid="loan-download-template"
                  >
                    <FileXls size={12} weight="bold" /> Download template
                  </button>
                </div>

                <PortfolioSummary loans={loans} />

                <NextResetBanner loans={loans} />

                {loans.map((l, i) => (
                  <LoanCard
                    key={l._k}
                    loan={l}
                    idx={i}
                    canDelete={true}
                    onChange={(k, v) => updLoan(i, k, v)}
                    onDelete={() => removeLoan(i)}
                    onUpdateRate={() => setRateDialog({ open: true, loanIdx: i })}
                  />
                ))}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={runCalc}
                    disabled={calc}
                    className="btn-primary px-6 py-3 text-sm flex-1 min-w-[160px]"
                    data-testid="loan-calc-btn"
                  >
                    {calc ? "Calculating…" : "Recalculate"}
                  </button>
                  <button
                    onClick={savePlan}
                    disabled={saving}
                    className="btn-ghost px-4 py-3 text-sm inline-flex items-center gap-2"
                    data-testid="loan-save-btn"
                  >
                    <FloppyDisk size={14} weight="duotone" /> {saving ? "…" : "Save"}
                  </button>
                </div>
              </div>

              <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
                {result ? (
                  <>
                    <WhatIfPanel
                      whatIf={whatIf}
                      setWhatIf={setWhatIf}
                      busy={whatIfBusy}
                      overlayActive={!!whatIfResult}
                      baseline={result}
                      view={view}
                      onReset={resetWhatIf}
                    />
                    <ResultPanel
                      result={view}
                      baseline={result}
                      overlayActive={!!whatIfResult}
                      onExport={exportFile}
                      exporting={exporting}
                      onAi={askAi}
                    />
                  </>
                ) : (
                  <div className="card-flat p-10 text-center text-muted-foreground" data-testid="loan-empty">
                    Hit <span className="text-foreground">Recalculate</span> to see total EMI, savings, and AI suggestions.
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="bonus-advisor" className="mt-6" data-testid="tab-content-bonus-advisor">
          {loans.length === 0 ? (
            <AddFirstLoanHint onGoToLoans={() => setTab("my-loans")} />
          ) : (
            <BonusAdvisor
              forceOpen
              loans={loans}
              planName={planName}
              onBalancesUpdated={async () => {
                await loadSaved();
                try {
                  const { data } = await api.get("/loans/saved");
                  const plan = (data || []).find((p) => p.name === planName);
                  if (plan) loadPlan(plan);
                } catch (err) {
                  if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.debug("plan reload failed after bonus apply:", err?.message);
                  }
                }
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="simulator" className="mt-6" data-testid="tab-content-simulator">
          {loans.length === 0 ? (
            <AddFirstLoanHint onGoToLoans={() => setTab("my-loans")} />
          ) : (
            <PrepaymentSimulator
              forceOpen
              key={prepayPrefill ? `sim-${prepayPrefill._k}` : "sim"}
              loans={loans}
              prefill={prepayPrefill}
              onApplied={({ loan_name, prepayment }) => {
                setLoans((ls) => ls.map((l) =>
                  l.name === loan_name
                    ? { ...l, current_balance: Math.max((Number(l.current_balance) || Number(l.principal)) - prepayment, 0) }
                    : l
                ));
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="rate-changes" className="mt-6" data-testid="tab-content-rate-changes">
          {loans.length === 0 ? (
            <AddFirstLoanHint onGoToLoans={() => setTab("my-loans")} />
          ) : (
            <RateChangesPanel
              loans={loans}
              planName={planName}
              onPortfolio={() => setPortfolioRateOpen(true)}
              onUpdate={(idx) => setRateDialog({ open: true, loanIdx: idx })}
            />
          )}
        </TabsContent>

        <TabsContent value="scenarios" className="mt-6" data-testid="tab-content-scenarios">
          <SavedScenariosPanel
            planName={planName}
            onGoSimulator={() => setTab("simulator")}
            onGoRateChanges={() => setTab("rate-changes")}
          />
        </TabsContent>

        <TabsContent value="progress" className="mt-6" data-testid="tab-content-progress">
          <ProgressReports loans={loans} planName={planName} userName="" />
        </TabsContent>

        <TabsContent value="ai-suggestions" className="mt-6" data-testid="tab-content-ai-suggestions">
          <AiSuggestionsPanel
            loans={loans}
            planName={planName}
            onOpenSimulator={(p) => { if (p) setPrepayPrefill({ ...p, _k: Date.now() }); setTab("simulator"); }}
          />
        </TabsContent>
      </Tabs>

      {aiOpen && (
        <AiSuggestionModal
          loading={aiLoading}
          ai={aiResult}
          onClose={() => setAiOpen(false)}
        />
      )}

      <BulkUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        planName={planName}
        onImported={onBulkImported}
      />

      <RateChangeDialog
        open={rateDialog.open}
        onClose={() => setRateDialog({ open: false, loanIdx: null })}
        loan={rateDialog.loanIdx !== null ? loans[rateDialog.loanIdx] : null}
        planName={planName}
        onApplied={({ loan_name, new_rate, new_emi, new_tenure_months, undone }) => {
          if (undone) {
            // soft reload — refresh plan from server
            loadSaved();
            return;
          }
          setLoans((ls) => ls.map((l) => l.name === loan_name
            ? {
                ...l,
                interest_rate_pct: Number(new_rate),
                current_emi: Number(new_emi),
                remaining_tenure_months: Number(new_tenure_months),
                tenure_years: Math.max(1, Math.round(Number(new_tenure_months) / 12)),
              }
            : l));
          setResult(null);
        }}
        onPreparePrepayment={({ loan_name, amount }) => {
          setPrepayPrefill({ loan_name, amount, _k: Date.now() });
          setRateDialog({ open: false, loanIdx: null });
          // scroll into simulator
          setTimeout(() => {
            document.getElementById("prepay-sim-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }}
      />

      <PortfolioRateChangeDialog
        open={portfolioRateOpen}
        onClose={() => setPortfolioRateOpen(false)}
        loans={payload.loans}
        planName={planName}
        onApplied={() => {
          loadSaved();
          setResult(null);
          setPortfolioRateOpen(false);
        }}
      />

      {tourStep > 0 && (
        <FirstTimeTour
          step={tourStep}
          onNext={() => setTourStep((s) => (s >= 5 ? 0 : s + 1))}
          onSkip={dismissTour}
          onDone={dismissTour}
        />
      )}
    </div>
  );
}

function EmptyLoansState({ onAdd, onUpload, onDownloadTemplate }) {
  return (
    <div className="card-flat p-10 text-center space-y-4" data-testid="loan-empty-state">
      <Bank size={44} weight="duotone" className="mx-auto text-[hsl(var(--primary))]/70" />
      <div>
        <h3 className="font-serif text-2xl mb-1">Start by adding a loan</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Enter your home, car, personal or education loan manually — or upload every loan at once with our Excel template.
        </p>
      </div>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <button onClick={onAdd} className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-1.5" data-testid="loan-empty-add">
          <Plus size={13} weight="bold" /> Add your first loan
        </button>
        <button onClick={onUpload} className="btn-ghost px-5 py-2.5 text-sm inline-flex items-center gap-1.5 border hairline rounded" data-testid="loan-empty-upload">
          <UploadSimple size={13} weight="bold" /> Upload from Excel
        </button>
        <button onClick={onDownloadTemplate} className="btn-ghost px-3 py-2.5 text-xs inline-flex items-center gap-1.5" data-testid="loan-empty-template">
          <FileXls size={12} weight="bold" /> Download template
        </button>
      </div>
    </div>
  );
}

function AddFirstLoanHint({ onGoToLoans }) {
  return (
    <div className="card-flat p-8 text-center space-y-3" data-testid="add-first-loan-hint">
      <WarningCircle size={32} weight="duotone" className="mx-auto text-[hsl(var(--secondary))]" />
      <h3 className="font-serif text-xl">Add a loan to use this tool</h3>
      <p className="text-sm text-muted-foreground">
        Head to <b>My Loans</b> and add at least one loan or upload your Excel.
      </p>
      <button onClick={onGoToLoans} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid="go-my-loans">
        <ArrowRight size={12} weight="bold" /> Go to My Loans
      </button>
    </div>
  );
}

function RateChangesPanel({ loans, planName, onUpdate, onPortfolio }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const nonOdLoans = useMemo(() => loans.filter((l) => l.kind !== "overdraft"), [loans]);
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/loans/rate-history", {
        params: planName ? { plan_name: planName } : {},
      });
      setHistory(data || []);
    } catch (err) {
      // 401 for anon users is expected; log in dev for anything else.
      if (process.env.NODE_ENV !== "production" && err?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.debug("rate-history fetch failed:", err?.response?.status || err?.message);
      }
    }
    setLoading(false);
  }, [planName]);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="rate-changes-panel">
      <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Percent size={13} weight="duotone" /> Rate changes
          </div>
          <p className="text-xs text-muted-foreground">
            Update a single loan's rate, or apply an RBI repo change to every floating-rate loan.
          </p>
        </div>
        <button
          onClick={onPortfolio}
          className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5"
          data-testid="loan-portfolio-rate-btn"
        >
          <TrendUp size={12} weight="bold" /> RBI changed the repo rate
        </button>
      </div>

      <div className="space-y-2">
        {nonOdLoans.map((l, idx) => {
          const bench = l.benchmark_type || "Other";
          const nextReset = l.next_reset_date || "";
          return (
            <div key={l._k} className="card-flat p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`rate-changes-row-${idx}`}>
              <div>
                <div className="font-serif text-base">{l.name}</div>
                <div className="text-xs text-muted-foreground">
                  {Number(l.interest_rate_pct).toFixed(2)}% · {formatINR(l.current_balance || l.principal, true)} outstanding · {bench}
                  {nextReset && <> · Resets {nextReset}</>}
                </div>
              </div>
              <button
                onClick={() => onUpdate(loans.indexOf(l))}
                className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5 border hairline rounded"
                data-testid={`rate-changes-update-${idx}`}
              >
                <Percent size={11} weight="duotone" /> Update rate
              </button>
            </div>
          );
        })}
      </div>

      <div className="card-flat p-4">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <Clock size={11} weight="duotone" /> History
        </div>
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-xs text-muted-foreground" data-testid="rate-history-empty">No rate changes yet.</div>
        ) : (
          <div className="space-y-1 text-[11px]" data-testid="rate-history-timeline">
            {history.map((h) => (
              <div key={h.id} className={`flex items-start justify-between border hairline p-2 flex-wrap gap-2 ${h.undone ? "opacity-50 line-through" : ""}`}>
                <div>
                  <b>{h.loan_name}</b> ·{" "}
                  {Number(h.old_rate).toFixed(2)}% → {Number(h.new_rate).toFixed(2)}%
                  <span className="text-muted-foreground"> · {h.reason}</span>
                </div>
                <div className="text-muted-foreground">
                  EMI ₹{Number(h.emi_before).toLocaleString("en-IN")} → ₹{Number(h.emi_after).toLocaleString("en-IN")}
                  {" · "} {h.effective_date}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SavedScenariosPanel({ planName, onGoSimulator, onGoRateChanges }) {
  const [prepay, setPrepay] = useState([]);
  const [bonus, setBonus] = useState([]);
  const [rate, setRate] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/loans/scenarios").catch(() => ({ data: [] })),
        api.get("/loans/bonus-scenarios").catch(() => ({ data: [] })),
        api.get("/loans/rate-history", { params: planName ? { plan_name: planName } : {} }).catch(() => ({ data: [] })),
      ]);
      setPrepay(a.data || []);
      setBonus(b.data || []);
      setRate(c.data || []);
    } finally {
      setLoading(false);
    }
  }, [planName]);
  useEffect(() => { refresh(); }, [refresh]);

  const deletePrepay = async (id) => {
    try {
      await api.delete(`/loans/scenarios/${id}`);
      await refresh();
    } catch {
      toast.error("Could not delete scenario");
    }
  };

  if (loading) return <div className="text-xs text-muted-foreground">Loading…</div>;

  const total = prepay.length + bonus.length + rate.length;
  if (total === 0) {
    return (
      <div className="card-flat p-10 text-center space-y-3" data-testid="scenarios-empty">
        <CheckCircle size={32} weight="duotone" className="mx-auto text-muted-foreground" />
        <h3 className="font-serif text-xl">Nothing saved yet</h3>
        <p className="text-sm text-muted-foreground">
          Save scenarios from the Prepayment Simulator or apply a rate change — they'll show up here.
        </p>
        <div className="flex gap-2 justify-center">
          <button onClick={onGoSimulator} className="btn-primary px-4 py-2 text-xs" data-testid="scenarios-go-sim">Open Simulator</button>
          <button onClick={onGoRateChanges} className="btn-ghost px-4 py-2 text-xs border hairline rounded" data-testid="scenarios-go-rate">Open Rate Changes</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="saved-scenarios-panel">
      {prepay.length > 0 && (
        <section className="card-flat p-4">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Calculator size={11} weight="duotone" /> Prepayment scenarios ({prepay.length})
          </div>
          <div className="space-y-1 text-xs">
            {prepay.map((s) => (
              <div key={s.id} className={`flex items-center justify-between border hairline p-2 flex-wrap gap-2 ${s.stale ? "border-destructive/40 bg-destructive/5" : ""}`}
                   data-testid={`scenario-prepay-${s.id}`}>
                <div>
                  <b>{s.name}</b>
                  <span className="text-muted-foreground"> · {s.loan_name} · saved {formatINR(s.summary?.net_saving || 0, true)}</span>
                  {s.stale && (
                    <span className="ml-2 text-[10px] text-destructive" data-testid={`scenario-prepay-stale-${s.id}`}>
                      · Rate changed — recalculate
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={onGoSimulator} className="text-[10px] underline hover:text-foreground" data-testid={`scenario-prepay-open-${s.id}`}>
                    Open
                  </button>
                  <button onClick={() => deletePrepay(s.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`scenario-prepay-del-${s.id}`}>
                    <Trash size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {bonus.length > 0 && (
        <section className="card-flat p-4">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Target size={11} weight="duotone" /> Bonus Advisor history ({bonus.length})
          </div>
          <div className="space-y-1 text-xs">
            {bonus.map((s) => (
              <div key={s.id} className="flex items-center justify-between border hairline p-2 flex-wrap gap-2" data-testid={`scenario-bonus-${s.id}`}>
                <div>
                  <b>{s.label}</b>
                  <span className="text-muted-foreground"> · {s.plan_name} · {new Date(s.saved_at).toLocaleDateString("en-IN")}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {rate.length > 0 && (
        <section className="card-flat p-4">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Percent size={11} weight="duotone" /> Rate change history ({rate.length})
          </div>
          <div className="space-y-1 text-xs">
            {rate.map((h) => (
              <div key={h.id} className={`flex items-start justify-between border hairline p-2 flex-wrap gap-2 ${h.undone ? "opacity-50 line-through" : ""}`}
                   data-testid={`scenario-rate-${h.id}`}>
                <div>
                  <b>{h.loan_name}</b> · {Number(h.old_rate).toFixed(2)}% → {Number(h.new_rate).toFixed(2)}%
                  <span className="text-muted-foreground"> · {h.reason}</span>
                </div>
                <div className="text-muted-foreground">
                  EMI ₹{Number(h.emi_before).toLocaleString("en-IN")} → ₹{Number(h.emi_after).toLocaleString("en-IN")} · {h.effective_date}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="text-[10px] text-muted-foreground">
        Estimates for planning only. Not financial advice. Confirm figures with your lender.
      </div>
    </div>
  );
}

function FirstTimeTour({ step, onNext, onSkip, onDone }) {
  const steps = [
    null,
    { title: "1. Add or upload your loans", body: "Enter one loan at a time, or upload every loan at once with our Excel template.", target: "my-loans" },
    { title: "2. Got a bonus? Ask the Advisor", body: "Tell Estima how much cash is coming in and we'll pick the loans to prepay for max interest saving or EMI relief.", target: "bonus-advisor" },
    { title: "3. Try your own prepayment", body: "Pick any amount, any month, any pattern — see interest saved, months cut and the new closure date live.", target: "simulator" },
    { title: "4. Rate changed? Update it here", body: "When your rate resets or the RBI moves the repo rate, update it and see the new EMI, tenure and interest instantly.", target: "rate-changes" },
    { title: "5. Track progress and download reports", body: "See principal and interest paid, outstanding, and future-due amounts for each loan and your whole portfolio. Download Progress and Bulk Payment reports as PDF or Excel.", target: "progress" },
  ];
  const s = steps[step];
  if (!s) return null;
  const isLast = step === 5;
  return (
    <div className="fixed inset-x-0 bottom-4 z-[10040] flex justify-center px-4" data-testid={`loan-tour-step-${step}`}>
      <div className="card-flat p-4 max-w-md shadow-xl border border-[hsl(var(--primary))]/40 bg-background/95 backdrop-blur">
        <div className="eyebrow mb-1 text-[9px] flex items-center justify-between">
          <span>Quick tour · Step {step} / 5</span>
          <button onClick={onSkip} className="text-muted-foreground hover:text-foreground" data-testid="loan-tour-skip">Skip</button>
        </div>
        <div className="font-serif text-base mb-1">{s.title}</div>
        <p className="text-xs text-muted-foreground mb-3">{s.body}</p>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={isLast ? onDone : onNext}
            className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1"
            data-testid={isLast ? "loan-tour-done" : "loan-tour-next"}
          >
            {isLast ? "Got it" : "Next"} <ArrowRight size={11} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function NextResetBanner({ loans }) {
  const soon = useMemo(() => {
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 86400000);
    return (loans || []).filter((l) => {
      if (!l.next_reset_date) return false;
      const d = new Date(l.next_reset_date);
      if (isNaN(d.getTime())) return false;
      return d >= now && d <= in30;
    });
  }, [loans]);
  if (!soon.length) return null;
  return (
    <div className="card-flat p-3 border border-[hsl(var(--secondary))]/60 text-xs" data-testid="loan-next-reset-banner">
      <div className="flex items-start gap-2">
        <TrendUp size={14} weight="duotone" className="text-[hsl(var(--secondary))] shrink-0 mt-0.5" />
        <div>
          <b>Rate reset coming up</b> ·{" "}
          {soon.map((l, i) => (
            <span key={l._k || l.name}>
              {i > 0 ? ", " : ""}<b>{l.name}</b> resets on {l.next_reset_date}
            </span>
          ))}
          . Update the rate once your bank confirms it.
        </div>
      </div>
    </div>
  );
}

function WhatIfPanel({ whatIf, setWhatIf, busy, overlayActive, onReset }) {
  const dirty = whatIf.pp_bonus || whatIf.emi_bump_per_mo || whatIf.tenure_cut_pct;
  return (
    <div className="card-flat p-5" data-testid="loan-whatif">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
        <div className="eyebrow">What-if · drag to explore</div>
        <div className="text-[10px] text-muted-foreground flex items-center gap-2">
          {busy && <span className="animate-pulse text-[hsl(var(--secondary))]">recalculating…</span>}
          {overlayActive && !busy && <span className="text-[hsl(var(--secondary))]">overlay active</span>}
          {dirty ? (
            <button onClick={onReset} className="underline hover:text-foreground" data-testid="loan-whatif-reset">
              reset
            </button>
          ) : null}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Pre-pay sweet-spots without clicking Recalculate. Sliders apply to every loan equally; the panel above updates in &lt;250 ms.
      </p>
      <div className="grid gap-3">
        <Slider
          label="Extra prepayment"
          suffix={`+₹${(whatIf.pp_bonus / 100000).toFixed(1)}L`}
          hint="One-time bonus added to every loan's existing part-payment"
          min={0}
          max={5000000}
          step={50000}
          value={whatIf.pp_bonus}
          onChange={(v) => setWhatIf((w) => ({ ...w, pp_bonus: v }))}
          tid="loan-whatif-pp"
        />
        <Slider
          label="EMI bump"
          suffix={`+₹${whatIf.emi_bump_per_mo.toLocaleString("en-IN")}/mo`}
          hint="Extra paid every month above the EMI — modelled as 12× annual prepayment"
          min={0}
          max={50000}
          step={500}
          value={whatIf.emi_bump_per_mo}
          onChange={(v) => setWhatIf((w) => ({ ...w, emi_bump_per_mo: v }))}
          tid="loan-whatif-bump"
        />
        <Slider
          label="Tenure cut"
          suffix={`−${whatIf.tenure_cut_pct}%`}
          hint="Trim every loan's remaining tenure by this percentage"
          min={0}
          max={50}
          step={1}
          value={whatIf.tenure_cut_pct}
          onChange={(v) => setWhatIf((w) => ({ ...w, tenure_cut_pct: v }))}
          tid="loan-whatif-tenure"
        />
      </div>
    </div>
  );
}

function Slider({ label, suffix, hint, min, max, step, value, onChange, tid }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="num-metric text-sm text-[hsl(var(--secondary))]">{suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[hsl(var(--secondary))]"
        data-testid={tid}
      />
      <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>
    </label>
  );
}

function LoanCard({ loan, idx, canDelete, onChange, onDelete, onUpdateRate }) {
  const isOd = loan.kind === "overdraft";
  return (
    <div className="card-flat p-4 space-y-3" data-testid={`loan-card-${idx}`}>
      <div className="flex items-center gap-3">
        <input
          value={loan.name}
          onChange={(e) => onChange("name", e.target.value)}
          className="input-dark px-3 py-2 text-sm flex-1"
          data-testid={`loan-name-${idx}`}
        />
        <select
          value={loan.kind}
          onChange={(e) => {
            onChange("kind", e.target.value);
            if (e.target.value === "overdraft" && !loan.dropline_years) onChange("dropline_years", 10);
          }}
          className="input-dark px-3 py-2 text-xs"
          data-testid={`loan-kind-${idx}`}
        >
          {KINDS.map((k) => (
            <option key={k.id} value={k.id}>{k.label}</option>
          ))}
        </select>
        {canDelete && (
          <button onClick={onDelete} className="text-muted-foreground hover:text-destructive p-2" aria-label="Remove loan" data-testid={`loan-del-${idx}`}>
            <X size={14} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <NumF label="Initial amount (₹)" v={loan.principal} onChange={(v) => onChange("principal", v)} tid={`loan-principal-${idx}`} />
        <NumF label="Outstanding (₹)" v={loan.current_balance} onChange={(v) => onChange("current_balance", v)} tid={`loan-balance-${idx}`} />
        <NumF label="Interest rate %" v={loan.interest_rate_pct} step="0.05" onChange={(v) => onChange("interest_rate_pct", v)} tid={`loan-rate-${idx}`} />
        <NumF
          label={isOd ? "Dropline (years)" : "Tenure (years)"}
          v={isOd ? loan.dropline_years : loan.tenure_years}
          onChange={(v) => onChange(isOd ? "dropline_years" : "tenure_years", v)}
          tid={`loan-tenure-${idx}`}
        />
      </div>

      {/* Feature 4 additive fields — benchmark + next reset */}
      {!isOd && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>
            <span className="block mb-1 text-muted-foreground">Benchmark type</span>
            <select
              value={loan.benchmark_type || "Other"}
              onChange={(e) => onChange("benchmark_type", e.target.value)}
              className="input-dark w-full px-2 py-1.5 text-xs"
              data-testid={`loan-benchmark-${idx}`}
            >
              {BENCHMARK_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label>
            <span className="block mb-1 text-muted-foreground">Next reset date (optional)</span>
            <input
              type="date"
              value={loan.next_reset_date || ""}
              onChange={(e) => onChange("next_reset_date", e.target.value)}
              className="input-dark w-full px-2 py-1.5 text-xs"
              data-testid={`loan-next-reset-${idx}`}
            />
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 pt-2 border-t hairline">
        <NumF label="Part-payment (₹)" v={loan.part_payment_amount} onChange={(v) => onChange("part_payment_amount", v)} tid={`loan-pp-${idx}`} />
        <label className="text-xs">
          <span className="block mb-1 text-muted-foreground">After part-payment</span>
          <select
            value={loan.part_payment_strategy}
            onChange={(e) => onChange("part_payment_strategy", e.target.value)}
            className="input-dark w-full px-2 py-1.5 text-xs disabled:opacity-50"
            disabled={isOd}
            data-testid={`loan-strategy-${idx}`}
          >
            {STRATEGIES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          {isOd && <span className="text-[10px] text-muted-foreground">OD always reduces effective EMI.</span>}
        </label>
      </div>

      {!isOd && onUpdateRate && (
        <button
          onClick={onUpdateRate}
          className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1.5 border hairline rounded w-full justify-center"
          data-testid={`loan-update-rate-${idx}`}
        >
          <Percent size={12} weight="duotone" /> Update interest rate
        </button>
      )}
    </div>
  );
}

function ResultPanel({ result, baseline, overlayActive, onExport, exporting, onAi }) {
  const nudge = result.prepay_vs_invest;
  return (
    <div className="space-y-4" data-testid="loan-result">
      <div className="card-flat p-5">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div className="eyebrow">Summary {overlayActive && <span className="text-[10px] ml-1 text-[hsl(var(--secondary))]">· what-if overlay</span>}</div>
          {overlayActive && baseline && (
            <div className="text-[10px] text-muted-foreground" data-testid="loan-overlay-delta">
              Δ interest saved:{" "}
              <span className="text-[hsl(var(--secondary))]">
                {(result.interest_saved - baseline.interest_saved) >= 0 ? "+" : "−"}₹
                {Math.abs(Math.round(result.interest_saved - baseline.interest_saved)).toLocaleString("en-IN")}
              </span>
              {"  ·  "}Δ EMI:{" "}
              <span className="text-foreground">
                {(result.total_emi_after - baseline.total_emi_after) >= 0 ? "+" : "−"}₹
                {Math.abs(Math.round(result.total_emi_after - baseline.total_emi_after)).toLocaleString("en-IN")}/mo
              </span>
            </div>
          )}
        </div>
        <Row label="Total EMI now" value={`₹${Number(result.total_emi_now).toLocaleString("en-IN")}/mo`} bold />
        <Row label="Total EMI after part-payment" value={`₹${Number(result.total_emi_after).toLocaleString("en-IN")}/mo`} tone="secondary" />
        <Row label="Monthly EMI saving" value={`₹${Number(result.monthly_emi_saving).toLocaleString("en-IN")}/mo`} tone="secondary" />
        <Row label="Interest saved" value={`₹${Number(result.interest_saved).toLocaleString("en-IN")}`} tone="secondary" big />
        <Row label="Part-payment deployed" value={`₹${Number(result.total_part_payment).toLocaleString("en-IN")}`} />
      </div>

      {nudge?.applicable && <PrepayVsInvestCard nudge={nudge} />}

      <div className="card-flat p-5">
        <div className="eyebrow mb-3">Per-loan breakdown</div>
        <div className="space-y-2 text-xs">
          {result.rows.map((r) => (
            <div key={r.id} className="border-b hairline last:border-0 pb-2" data-testid={`loan-row-${r.id}`}>
              <div className="flex justify-between">
                <span className="font-serif text-base">{r.name}</span>
                <span className="text-muted-foreground capitalize">{r.kind}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>EMI ₹{Number(r.emi_now).toLocaleString("en-IN")} → ₹{Number(r.emi_after).toLocaleString("en-IN")}</span>
                <span className="text-[hsl(var(--secondary))]">−₹{Number(r.interest_saved).toLocaleString("en-IN")}</span>
              </div>
              {r.tenure_saved_months > 0 && (
                <div className="text-[10px] text-muted-foreground">
                  Tenure cut by {Math.round(r.tenure_saved_months / 12 * 10) / 10} years
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={() => onExport("pdf")}
          disabled={exporting === "pdf"}
          className="btn-ghost px-3 py-3 text-xs inline-flex items-center justify-center gap-1.5"
          data-testid="loan-export-pdf"
        >
          <FilePdf size={14} weight="duotone" /> {exporting === "pdf" ? "…" : "PDF"}
        </button>
        <button
          onClick={() => onExport("xlsx")}
          disabled={exporting === "xlsx"}
          className="btn-ghost px-3 py-3 text-xs inline-flex items-center justify-center gap-1.5"
          data-testid="loan-export-xlsx"
        >
          <FileXls size={14} weight="duotone" /> {exporting === "xlsx" ? "…" : "Excel"}
        </button>
        <button
          onClick={onAi}
          className="btn-secondary px-3 py-3 text-xs inline-flex items-center justify-center gap-1.5"
          data-testid="loan-ai-btn"
        >
          <Sparkle size={14} weight="duotone" /> AI tip
        </button>
      </div>
    </div>
  );
}

function AiSuggestionModal({ loading, ai, onClose }) {
  const paragraphs = ai?.narrative ? ai.narrative.split(/\n\s*\n/).filter(Boolean) : [];
  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4" data-testid="loan-ai-modal">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <div className="relative card-flat w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground" data-testid="loan-ai-close">
          <X size={16} weight="bold" />
        </button>
        <div className="eyebrow mb-1 flex items-center gap-2">
          <Sparkle size={14} weight="duotone" /> AI suggestion
        </div>
        <h2 className="font-serif text-2xl mb-4">Which loan should I attack first?</h2>

        {loading && <p className="text-sm text-muted-foreground">Claude is thinking…</p>}

        {!loading && ai?.error && (
          <div className="text-sm text-destructive flex items-start gap-2">
            <WarningCircle size={16} weight="duotone" className="shrink-0 mt-0.5" /> {ai.error}
          </div>
        )}

        {!loading && ai?.narrative && (
          <div className="space-y-4">
            {paragraphs.map((p, i) => (
              <p key={`p-${i}-${p.slice(0, 12)}`} className="text-sm leading-relaxed text-foreground">{p}</p>
            ))}
            {ai.disclaimer && (
              <div className="border-t hairline pt-3 mt-4 text-[11px] text-muted-foreground flex items-start gap-2">
                <WarningCircle size={12} weight="duotone" className="shrink-0 mt-0.5" />
                {ai.disclaimer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PrepayVsInvestCard({ nudge }) {
  const isInvest = nudge.winner === "invest";
  const tone = isInvest ? "text-[hsl(var(--secondary))]" : "text-foreground";
  return (
    <div
      className={`card-flat p-5 border ${isInvest ? "border-[hsl(var(--secondary))]/60" : "border-[hsl(var(--primary))]/60"}`}
      data-testid="loan-prepay-vs-invest"
    >
      <div className="eyebrow mb-2 flex items-center gap-2">
        <Sparkle size={12} weight="duotone" className={tone} />
        <span>Prepay vs Invest</span>
      </div>
      <div className="font-serif text-xl mb-2" data-testid="loan-prepay-vs-invest-winner">
        {isInvest ? "Invest the lumpsum instead." : "Prepay first."}
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-3">{nudge.verdict}</p>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="border hairline p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Prepay saves</div>
          <div className="num-metric text-base">₹{Number(nudge.interest_saved_by_prepay).toLocaleString("en-IN")}</div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            SIP @ {nudge.sip_annual_pct}% earns
          </div>
          <div className="num-metric text-base">₹{Number(nudge.investment_gain).toLocaleString("en-IN")}</div>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
        Compares ₹{Number(nudge.part_payment).toLocaleString("en-IN")} prepayment savings vs the
        same lumpsum invested at {nudge.sip_annual_pct}% p.a. for {nudge.horizon_years} years
        (long-run Indian equity assumption). Tax, expense ratio, and liquidity vary — treat as
        guidance, not advice.
      </p>
    </div>
  );
}


function NumF({ label, v, onChange, tid, step }) {
  return (
    <label>
      <span className="block mb-1 text-muted-foreground">{label}</span>
      <input
        type="number"
        step={step || "1"}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="input-dark w-full px-2 py-1.5 text-sm"
        data-testid={tid}
      />
    </label>
  );
}

function Row({ label, value, bold, big, tone }) {
  const toneCls = tone === "secondary" ? "text-[hsl(var(--secondary))]" : "text-foreground";
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${big ? "num-metric text-xl" : ""} ${bold ? "font-medium" : ""} ${toneCls}`}>
        {value}
      </span>
    </div>
  );
}
